import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { api } from '../../services/api';
import { fmtMoney, fmtMoneyShort, monthLabelShort, fmtMonth } from './format';
import { card, chart } from '../../theme';

/**
 * CategoryMixTrend — how the spend MIX shifts month to month.
 *
 * This is the one chart here where categorical color earns its place: the
 * series are the subject, so the reader has to tell them apart. The ranked bar
 * charts deliberately use a single hue instead, because there the length is
 * the whole message.
 *
 * ── Palette ─────────────────────────────────────────────────────────────────
 * Seven fixed slots, validated against this app's white card surface with the
 * data-viz validator (light mode, surface #FFFFFF):
 *     lightness band PASS · chroma floor PASS
 *     CVD separation PASS  — worst adjacent pair ΔE 9.1 (protan)
 *     normal-vision    PASS — worst adjacent pair ΔE 19.6
 *     contrast         WARN — aqua/yellow/magenta sit under 3:1 on white, which
 *                             obligates relief: this chart ships a legend AND
 *                             the Breakdown panel beside it is a table view of
 *                             the identical numbers.
 * Seven is the ceiling, not a preference — past ~7 classes adjacent hues blur,
 * so the tail folds into a neutral "Other" rather than generating an 8th hue.
 */
// Now sourced from theme.js so the app has one categorical scale rather
// than a per-chart one. The validator output documented above is for that
// shared set; see theme.js for the co-visible variant used by donuts.
const PALETTE = chart.categorical;
const OTHER_COLOR = chart.other;
const OTHER_LABEL = 'Other';
const MAX_SERIES  = PALETTE.length;

export default function CategoryMixTrend({ filterParams, dimension = 'spend_category', title, subtitle }) {
  // Hue assignment is derived from the UNFILTERED ranking and then frozen, so
  // that color follows the entity rather than its current rank. Without this,
  // filtering would repaint the survivors and a reader who learned
  // "Entertainment is blue" would be misled the moment they narrowed the view.
  const { data: order } = useQuery({
    queryKey: ['expense-category-order', dimension],
    queryFn:  () => api.get('/expenses/breakdown', { params: { dimension, limit: 500 } }),
    staleTime: 10 * 60 * 1000,
  });

  const colorOf = useMemo(() => {
    const ranked = (order?.rows || []).map((r) => r.label);
    const map = new Map();
    ranked.slice(0, MAX_SERIES).forEach((label, i) => map.set(label, PALETTE[i]));
    return (label) => map.get(label) || OTHER_COLOR;
  }, [order]);

  const topSet = useMemo(
    () => new Set((order?.rows || []).slice(0, MAX_SERIES).map((r) => r.label)),
    [order]
  );

  const { data, isFetching } = useQuery({
    queryKey: ['expense-mix-trend', dimension, filterParams],
    queryFn:  () => api.get('/expenses/trend', { params: { ...filterParams, group_by: dimension } }),
    keepPreviousData: true,
  });

  // Pivot long rows (month, series, amount) into one object per month, folding
  // everything outside the seven fixed slots into Other.
  const { chartData, series } = useMemo(() => {
    const rows = data?.rows || [];
    const byMonth = new Map();
    const present = new Set();
    for (const r of rows) {
      const key = topSet.has(r.series) ? r.series : OTHER_LABEL;
      present.add(key);
      const m = byMonth.get(r.month) || { month: r.month, label: monthLabelShort(r.month) };
      m[key] = (m[key] || 0) + Number(r.net_amount);
      byMonth.set(r.month, m);
    }
    // Stack order follows the frozen ranking so the bands never reshuffle.
    const ordered = (order?.rows || [])
      .map((r) => r.label)
      .filter((l) => present.has(l))
      .slice(0, MAX_SERIES);
    if (present.has(OTHER_LABEL)) ordered.push(OTHER_LABEL);
    return {
      chartData: [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month)),
      series: ordered,
    };
  }, [data, topSet, order]);

  if (!isFetching && chartData.length === 0) {
    return <div style={s.card}><div style={s.title}>{title}</div><div style={s.empty}>No transactions match these filters.</div></div>;
  }

  return (
    <div style={{ ...s.card, opacity: isFetching ? 0.6 : 1 }}>
      <div style={s.head}>
        <div style={s.title}>{title}</div>
        <div style={s.subtitle}>{subtitle}</div>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} margin={{ top: 4, right: 12, bottom: 4, left: 4 }}>
          <CartesianGrid vertical={false} stroke={chart.grid} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: chart.axisText }} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={fmtMoneyShort} tick={{ fontSize: 10, fill: chart.axisText }}
                 axisLine={false} tickLine={false} width={56} />
          <Tooltip
            wrapperStyle={{ pointerEvents: 'none' }}
            isAnimationActive={false}
            cursor={{ fill: 'rgba(27,42,74,0.05)' }}
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const total = payload.reduce((a, p) => a + Number(p.value || 0), 0);
              return (
                <div style={s.tip}>
                  <div style={s.tipName}>{fmtMonth(payload[0]?.payload?.month)}</div>
                  {[...payload].reverse().map((p) => (
                    <div key={p.dataKey} style={s.tipRow}>
                      <span style={{ ...s.swatch, background: p.fill }} />
                      {/* Text stays in ink tokens; the swatch carries identity. */}
                      <span style={s.tipLabel}>{p.dataKey}</span>
                      <span style={s.tipAmt}>{fmtMoney(p.value)}</span>
                    </div>
                  ))}
                  <div style={s.tipTotal}><span>Total</span><span>{fmtMoney(total)}</span></div>
                </div>
              );
            }}
          />
          <Legend iconType="circle" iconSize={7} wrapperStyle={{ paddingTop: 6 }}
                  formatter={(v) => <span style={{ fontSize: 10.5, color: '#6B7280' }}>{v}</span>} />
          {series.map((name, i) => (
            <Bar key={name} dataKey={name} stackId="mix"
                 fill={colorOf(name)}
                 // 2px surface-colored gap between stacked segments so adjacent
                 // fills never touch — required separation, and it doubles as
                 // secondary encoding for the CVD floor band.
                 stroke="#FFFFFF" strokeWidth={2}
                 radius={i === series.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

const s = {
  card:     { ...card.sm, padding: '14px 16px 8px', transition: 'opacity 0.15s' },
  head:     { marginBottom: '8px' },
  title:    { fontSize: '13px', fontWeight: 600, color: '#111827' },
  subtitle: { fontSize: '11px', color: '#9CA3AF', marginTop: '2px' },
  empty:    { padding: '48px 8px', textAlign: 'center', fontSize: '12px', color: '#9CA3AF' },
  tip:      { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '9px 11px', boxShadow: '0 4px 14px rgba(0,0,0,0.08)', minWidth: '230px' },
  tipName:  { fontSize: '11.5px', fontWeight: 600, color: '#111827', marginBottom: '5px' },
  tipRow:   { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', padding: '1px 0' },
  swatch:   { width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0 },
  tipLabel: { flex: 1, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  tipAmt:   { color: '#111827', fontWeight: 500, fontVariantNumeric: 'tabular-nums' },
  tipTotal: { display: 'flex', justifyContent: 'space-between', marginTop: '5px', paddingTop: '5px', borderTop: '1px solid #F3F4F6', fontSize: '11px', fontWeight: 600, color: '#111827' },
};
